import { DependencyList, useCallback, useEffect, useState } from 'react'

interface IUseApiResult<T> {
  data: T | undefined
  loading: boolean
  error: Error | undefined
  refetch: () => Promise<void>
}

/**
 * Minimal data hook for the Core REST API (replaces the generated Apollo hooks).
 * Re-fetches when the dependency list changes.
 */
export const useApi = <T,>(fetcher: () => Promise<T>, deps: DependencyList = []): IUseApiResult<T> => {
  const [data, setData] = useState<T | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | undefined>(undefined)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(undefined)
    try {
      setData(await fetcher())
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    load()
  }, [load])

  return { data, loading, error, refetch: load }
}
